<script lang="ts">

  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "$lib/components/ui/card"
  import { FieldLabel, FieldMessage } from "$lib/components/ui/field"
  import { Input } from "$lib/components/ui/input"
  import type { _response } from "$lib/data/type/_response"
  import { APP_CODE, APP_VERSION } from "$lib/env"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { sleep } from "$lib/sleep"
  import { deviceStore } from "$lib/stores/device-store.svelte"
  import EyeIcon from "@lucide/svelte/icons/eye"
  import EyeOffIcon from "@lucide/svelte/icons/eye-off"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import { createForm } from "@tanstack/svelte-form"
  import { createMutation, createQuery, useQueryClient } from "@tanstack/svelte-query"
  import { toast } from "svelte-sonner"
  import { z } from "zod/v4"
  import DialogPasswordForgot from "./_components/dialog-password-forgot.svelte"

  const FormSchema = z.object({
    username: z.string().default(""),
    password: z.string().default(""),
    password_show: z.boolean().default(false),
    device_timezone: z.string().default(""),
  })
  type FormData = z.infer<typeof FormSchema>
  const FormEmpty = FormSchema.parse({})

  const queryClient = useQueryClient()

  const device = $derived(deviceStore.device)

  const form = createForm(() => ({
    defaultValues: FormEmpty,
  }))
  const formWatch = form.useSelector((state) => state.values)

  const passwordForgotDialog = useDialog()

  createQuery(() => ({
    queryKey: ["auth/login"],
    queryFn: async () => {

      await sleep(1)
      form.reset(FormEmpty)

      return ""
    }
  }))

  const mutation = createMutation(() => ({
    mutationFn: async (data: FormData) => {

      const response = await httpClient.post<_response<string>>("/api/auth/login", {
        ...data,
        device_id: device.device_id,
        device_name: device.device_name,
        device_timezone: device.region_id,
      })
      const responseData = response.data!

      form.reset(form.state.values)
      Object.entries(responseData.errors ?? {}).forEach(([field, messages]) => {
        form.setFieldMeta(field as never, (prev) => ({
          ...prev,
          errorMap: { onSubmit: { message: messages[0] } },
        }))
      })

      if (response.status !== 200) {
        return
      }

      void queryClient.invalidateQueries({ queryKey: ["_app"] })
    },
    onError: (error: Error) => {
      toast.error("Terjadi kesalahan", {
        description: error.message
      })
    }
  }))

</script>
<main class="relative flex h-full w-full overflow-y-scroll flex-col items-center justify-center">
  <div class="absolute top-4 inset-e-4 text-end font-mono text-xs font-light text-gray-800">
    <div>{APP_CODE}-{APP_VERSION}</div>
    <div>{deviceStore.device.device_id.toUpperCase().substring(0, 16)}</div>
  </div>
  <div class="relative w-85">
    <div class="absolute -top-32 w-full flex justify-center mb-4">
      <img
        src="/assets/logo.png"
        alt="logo"
        class="size-24 bg-transparent"
      />
    </div>
    <form
      action="#"
      autocomplete="off"
      onsubmit={(e) => {
        e.preventDefault()
        mutation.mutate(form.state.values)
      }}>
      <Card class="text-start border-none shadow-none md:border md:border-solid md:shadow!">
        <CardHeader>
          <CardTitle class="text-2xl">Login</CardTitle>
          <CardDescription>
            Isi form berikut untuk masuk kedalam aplikasi.
          </CardDescription>
        </CardHeader>
        <CardContent class="grid gap-4">
          <form.Field name="username">
            {#snippet children(field)}
              <div>
                <FieldLabel>Username</FieldLabel>
                <Input
                  type="text"
                  name={field.name}
                  autocapitalize="off"
                  autocomplete="username"
                  placeholder="username"
                  value={field.state.value}
                  oninput={(e) => field.handleChange(e.currentTarget.value)}
                />
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
          <form.Field name="password">
            {#snippet children(field)}
              {@const show = form.useSelector((state) => state.values.password_show)}
              <div>
                <div class="flex justify-between">
                  <FieldLabel>Kata Sandi</FieldLabel>
                  <Button
                    variant="link"
                    class="h-auto p-0 font-light"
                    onclick={() => {
                      passwordForgotDialog.setOpen(true)
                    }}>
                    Lupa Kata Sandi?
                  </Button>
                </div>
                <div class="relative">
                  <Input
                    type={show.current ? "text" : "password"}
                    name={field.name}
                    class="pe-9"
                    autocapitalize="off"
                    autocomplete="current-password"
                    value={field.state.value}
                    oninput={(e) => field.handleChange(e.currentTarget.value)}
                    placeholder="••••••••"
                  />
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    class="absolute top-0.5 inset-e-0.5 group"
                    data-show={show.current}
                    onclick={() => {
                      form.setFieldValue("password_show", !form.getFieldValue("password_show"))
                    }}>
                    <EyeOffIcon class="size-4 group-data-[show=true]:block hidden"/>
                    <EyeIcon class="size-4 group-data-[show=false]:block hidden"/>
                  </Button>
                </div>
                <FieldMessage name={field.name} {form}/>
              </div>
            {/snippet}
          </form.Field>
        </CardContent>
        <CardFooter class="flex-col gap-2">
          <Button
            type="submit"
            class="w-full group"
            data-loading={mutation.isPending}
            disabled={mutation.isPending}
          >
            <span class="group-data-[loading=true]:invisible">Masuk</span>
            <LoaderCircleIcon class="group-data-[loading=true]:visible invisible absolute animate-spin"/>
          </Button>
        </CardFooter>
      </Card>
    </form>
  </div>
  <div class="absolute bottom-0 mb-6 text-center text-sm font-light italic text-gray-600 md:mt-6">
    © 2026 supported by sinergics
  </div>
</main>
<DialogPasswordForgot
  open={passwordForgotDialog.state}
  setOpen={passwordForgotDialog.setOpen}
/>
